import { useState, useEffect } from 'react';
import { BrandSelect } from '@/components/BrandSelect';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { useSession } from '@/lib/auth';
import { useActiveLocationId } from '@/lib/locationContext';
import { isValidEmail, isValidPhone, formatPhone, fetchSuiteLabels, COMMON_AREAS } from '@/lib/platform';
import { randomUUID } from '@/lib/uuid';
import { isCell } from '@/lib/cell/config';
import { cellGet, cellPost } from '@/lib/cell/api';
import { cellUpload } from '@/lib/cell/files';
import { storageAddress } from '@/lib/storagePhoto';

/**
 * Maintenance Request — Wave 3 of n8n elimination.
 *
 * Direct Supabase: photos to Supabase Storage, then the ticket through public.ticket_file() —
 * THE ONE DOOR every source files through (FACILITY-1, 2026-09-23). Database webhook fires
 * `ticket-notifications` Edge Function which sends the Pro confirmation email + APNs push to
 * owners.
 *
 * ONE CATEGORY, NOT SEVERAL. The form used to let a pro tick every category that applied and
 * stored them joined with " | " — which read fine to a person and could not be routed to a
 * trade. Peter, 2026-09-23: one category per ticket, the labels exactly as pros know them. The
 * labels come from public.ticket_category so the Hub, the phones and the router agree; the
 * list below is the fallback when that read fails, and a test pins the two to each other.
 *
 * Per ADR-017 (tickets are Supabase source of truth) + ADR-020 (n8n out
 * for non-Zoho forms).
 */
export const CATEGORY_FALLBACK: ReadonlyArray<{ slug: string; label: string }> = [
  { slug: 'plumbing',   label: 'Plumbing (sink, toilet, water leak)' },
  { slug: 'electrical', label: 'Electrical (outlet, wiring, breaker)' },
  { slug: 'hvac',       label: 'HVAC (heating, cooling, thermostat)' },
  { slug: 'lighting',   label: 'Lighting (bulbs, fixtures, dimmer)' },
  { slug: 'locks',      label: 'Door / Lock (stuck, broken, key issue)' },
  { slug: 'cleaning',   label: 'Cleaning (deep clean, stains, odor)' },
  { slug: 'pest',       label: 'Pest Control' },
  { slug: 'wifi',       label: 'Internet / WiFi' },
  { slug: 'cabinetry',  label: 'Cabinetry / Countertop' },
  { slug: 'glass',      label: 'Mirror / Glass' },
  { slug: 'flooring',   label: 'Flooring' },
  { slug: 'walls',      label: 'Paint / Walls' },
  { slug: 'appliance',  label: 'Appliance (washer, dryer, fridge)' },
  { slug: 'security',   label: 'Security System' },
  { slug: 'other',      label: 'Other' },
];

const URGENCIES = [
  { value: 'Low', label: '🟢 Low' },
  { value: 'Medium', label: '🟡 Medium' },
  { value: 'High', label: '🟠 High' },
  { value: 'Emergency', label: '🔴 Emergency' },
];

const TIME_SLOTS = [
  '', 'Morning (before 10am)', 'Midday (10am - 2pm)', 'Afternoon (2pm - 6pm)',
  'Evening (after 6pm)', 'Weekday only', 'Weekend only', "Anytime — I'm flexible",
];

type FormState = {
  first_name: string; last_name: string; phone: string; email: string;
  suite: string; category: string; urgency: string;
  description: string; preferred_time: string; photos: string[];
  _hp: string;
};

const EMPTY: FormState = {
  first_name: '', last_name: '', phone: '', email: '',
  suite: '', category: '', urgency: '',
  description: '', preferred_time: '', photos: [], _hp: '',
};

/** The picked label, read: a known suite id (the reporter's own, or null for a common area / unknown label), or a
 * suite number that still has to be looked up at the studio. */
export function pickedSuite(
  picked: string,
  ctx: { suiteId: string | null; suiteNumber: string | null },
): { id: string | null } | { number: string } {
  const m = /^Suite\s+(.+)$/i.exec(picked.trim());
  if (!m) return { id: null };
  const number = m[1].trim();
  if (ctx.suiteNumber && number === ctx.suiteNumber) return { id: ctx.suiteId };
  return { number };
}

/** "Suite 103" becomes that suite's id at the studio; a common area or an unknown label gives null. The
 * reporter's own suite id is used when the label is theirs, saving the read. */
async function resolveSuiteId(
  locationId: string,
  picked: string,
  ctx: { suiteId: string | null; suiteNumber: string | null },
): Promise<string | null> {
  const p = pickedSuite(picked, ctx);
  if (!('number' in p)) return p.id;
  const number = p.number;
  // CELL (M28): production only — the cell path is the isCell() branch above, which returns first.
  const { data } = await supabase
    .from('suites').select('id').eq('location_id', locationId).eq('suite_number', number).limit(1).maybeSingle();
  return (data as { id: string } | null)?.id ?? null;
}

export default function Tickets() {
  const { session } = useSession();
  const { activeLocationId } = useActiveLocationId();

  // Per-location suite labels (off-CRM/MT fix 2026-07-11): was the
  // hardcoded Sharon 101–128 list for every location.
  const { data: suiteLabels = [] } = useQuery({
    queryKey: ['suite-labels', activeLocationId],
    queryFn: () => fetchSuiteLabels(activeLocationId),
  });
  const userId = session?.user?.id;

  // The category vocabulary, from the one table. Falls back to the pinned list so the form is
  // never empty; the labels are identical either way.
  const { data: categories = CATEGORY_FALLBACK } = useQuery({
    queryKey: ['ticket-categories'],
    queryFn: async () => {
      // CELL (M28): GET /v1/pro-hub/ticket-categories — the same read (slug, label; active, in order).
      const { data, error } = isCell()
        ? await cellGet<{ rows: Array<{ slug: string; label: string }> }>('/v1/pro-hub/ticket-categories')
          .then((r) => ({ data: r.data?.rows ?? null, error: r.error }))
        : await supabase
          .from('ticket_category').select('slug, label').eq('active', true).order('sort');
      if (error || !data?.length) return CATEGORY_FALLBACK;
      return data as Array<{ slug: string; label: string }>;
    },
  });

  const [f, setF] = useState<FormState>(EMPTY);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [errMsg, setErrMsg] = useState('');
  const [ticketId, setTicketId] = useState<string>('');
  /** The merge note to show (HUB-024), or null when this report opened its own ticket. */
  const [merged, setMerged] = useState<string | null>(null);
  const [proCtx, setProCtx] = useState<{ proId: string; tenantId: string | null; locationId: string; suiteId: string | null; suiteNumber: string | null } | null>(null);

  // Resolve Pro identity once on mount: pro.id, primary tenant.id + location_id + suite_id.
  // Pre-fills the form's name/phone/email and gives us the foreign keys for INSERT.
  useEffect(() => {
    if (!userId) return;
    (async () => {
      type Pro = { id: string; email: string | null; phone: string | null; first_name: string | null; last_name: string | null };
      type Tenant = { id: string; location_id: string; suite_id: string | null; suite_number: string | number | null; email: string | null; phone: string | null; first_name: string | null; last_name: string | null };
      let pro: Pro | null; let tenant: Tenant | null;
      if (isCell()) {
        // CELL (M28): GET /v1/pro-hub/users/{authUserId}/ticket-context — both reads below (pros, then tenants) in one.
        const { data } = await cellGet<{ pro: Pro | null; tenant: Tenant | null }>(
          `/v1/pro-hub/users/${userId}/ticket-context`, { location: activeLocationId });
        pro = data?.pro ?? null; tenant = data?.tenant ?? null;
        if (!pro) return;
      } else {
      ({ data: pro } = await supabase
        .from('pros').select('id, email, phone, first_name, last_name')
        .eq('auth_user_id', userId).maybeSingle());
      if (!pro) return;
      // Multi-location Pros: scope tenant lookup to the active location
      // selection so they file tickets against the right studio. Falls
      // back to most-recent if no location context yet.
      // CELL (M28): production only — the cell path is the isCell() branch above, which returns first.
      const tenantQuery = supabase
        .from('tenants').select('id, location_id, suite_id, suite_number, email, phone, first_name, last_name')
        .eq('pro_id', pro.id);
      ({ data: tenant } = activeLocationId
        ? await tenantQuery.eq('location_id', activeLocationId).maybeSingle()
        : await tenantQuery.order('updated_at', { ascending: false }).limit(1).maybeSingle());
      }
      if (!tenant) return;
      setProCtx({
        proId: pro.id, tenantId: tenant.id, locationId: tenant.location_id, suiteId: tenant.suite_id,
        suiteNumber: tenant.suite_number != null ? String(tenant.suite_number) : null,
      });
      // Pre-fill form fields from tenant (more authoritative than pro)
      setF(prev => ({
        ...prev,
        first_name: tenant.first_name ?? pro.first_name ?? '',
        last_name:  tenant.last_name  ?? pro.last_name  ?? '',
        // HUB-067: the address the confirmation ACTUALLY goes to — the same order
        // ticket-notifications resolves it (pros.email first, then tenants.email). It is shown,
        // not edited: ticket_file takes no email, so a typed one was silently ignored.
        email:      pro.email         ?? tenant.email   ?? '',
        phone:      tenant.phone ? formatPhone(tenant.phone) : (pro.phone ? formatPhone(pro.phone) : ''),
        // WE KNOW THE SUITE — prefill it (Peter: 'we know what the suite number is, so that
        // should be prefilled'). Matches the option labels fetchSuiteLabels builds.
        suite:      tenant.suite_number ? `Suite ${tenant.suite_number}` : prev.suite,
      }));
    })();
  }, [userId, activeLocationId]);

  function set<K extends keyof FormState>(k: K, v: FormState[K]) {
    setF(p => ({ ...p, [k]: v }));
    if (errors[k as string]) setErrors(p => ({ ...p, [k as string]: '' }));
  }


  async function onPhotoChange(files: FileList | null) {
    if (!files) return;
    const next = [...f.photos];
    for (const file of Array.from(files).slice(0, 3 - next.length)) {
      const compressed = await compressImage(file);
      next.push(compressed);
    }
    set('photos', next);
  }

  function validate(): boolean {
    const e: Record<string, string> = {};
    if (!f.first_name.trim()) e.first_name = 'Required';
    if (!f.last_name.trim()) e.last_name = 'Required';
    if (!isValidPhone(f.phone)) e.phone = 'Valid phone required';
    if (!f.suite) e.suite = 'Required';
    if (!f.category) e.category = 'Pick the one that fits best';
    if (!f.urgency) e.urgency = 'Required';
    if (!f.description.trim()) e.description = 'Required';
    setErrors(e);
    return Object.keys(e).length === 0;
  }

  async function handleSubmit(ev: React.FormEvent) {
    ev.preventDefault();
    if (f._hp) return;
    if (!validate()) return;
    if (!proCtx) {
      setStatus('error');
      setErrMsg("Couldn't determine your studio — try refreshing.");
      return;
    }
    setStatus('loading'); setErrMsg('');

    try {
      // Generate a draft ticket UUID upfront so we can scope photo paths
      // under it before the row exists. Storage path:
      //   maintenance-photos/{location_id}/{ticket_id}/{i}-{filename}.jpg
      const draftId = randomUUID();
      const photoUrls: string[] = [];

      for (let i = 0; i < f.photos.length; i++) {
        // f.photos[i] is a data URL (data:image/jpeg;base64,...). Decode to Blob for upload.
        const dataUrl = f.photos[i];
        const byteString = atob(dataUrl.split(',')[1]);
        const bytes = Uint8Array.from(byteString, (c) => c.charCodeAt(0));
        const blob = new Blob([bytes], { type: 'image/jpeg' });
        const path = `${proCtx.locationId}/${draftId}/${i + 1}-photo.jpg`;
        // CELL (M28): POST /v1/pro-hub/files/upload, then a PUT straight to S3 (the maintenance-photos upload rule).
        const { error: upErr } = isCell()
          ? await cellUpload('maintenance-photos', path, blob, 'image/jpeg')
          : await supabase.storage
            .from('maintenance-photos').upload(path, blob, {
              contentType: 'image/jpeg', upsert: false,
            });
        if (upErr) throw upErr;
        // Use a signed URL valid for 1 year — enough for owners to view photos
        // throughout the ticket lifecycle without exposing the bucket publicly.
        // CELL (M28; e33f5778, AWSM-301): the cell caps a link at 7 days (files.ts), so the cell build stores the photo's
        // ADDRESS (production's origin, the authenticated form, as the cell's maintenance-intake does) and the owner web
        // (/files/open) and the phones sign it on open. Production stores its year-long link until the cut.
        if (isCell()) photoUrls.push(storageAddress('maintenance-photos', path));
        else {
          const { data: signed } = await supabase.storage
            .from('maintenance-photos').createSignedUrl(path, 60 * 60 * 24 * 365);
          if (signed?.signedUrl) photoUrls.push(signed.signedUrl);
        }
      }

      // THE SUITE THE PROBLEM IS IN. The picked suite/common-area is WHERE THE ISSUE IS — it may
      // differ from the reporter's own suite (fixed 2026-07-11 in the description; carried as the
      // row's suite since FACILITY-1 so a second report on the same suite and trade joins the
      // open ticket instead of opening another). A common area has no suite row, so null.
      const label = categories.find(c => c.slug === f.category)?.label ?? f.category;
      const title = label.split(' (')[0];                       // "Plumbing" from "Plumbing (sink, …)"
      // CELL (M28): the suite lookup runs inside the filing endpoint below (one call for the action).
      const suiteId = isCell() ? null : await resolveSuiteId(proCtx.locationId, f.suite, proCtx);
      const picked = pickedSuite(f.suite, proCtx);

      // CELL (M28): POST /v1/pro-hub/locations/{locationId}/tickets — ticket_file with the same arguments.
      const { data: filed, error: fileErr } = isCell()
        ? await cellPost<{ ticket_number?: string; merged?: boolean; photos_added?: number }>(
          `/v1/pro-hub/locations/${encodeURIComponent(proCtx.locationId)}/tickets`, {
            p_category:      f.category,
            p_priority:      f.urgency,
            p_title:         title,
            p_description:   `Issue location: ${f.suite}\n\n${f.description}`,
            p_suite_id:      'number' in picked ? null : picked.id,
            suite_number:    'number' in picked ? picked.number : null,
            p_pro_id:        proCtx.proId,
            p_tenant_id:     proCtx.tenantId,
            p_photos:        photoUrls,
            p_preferred_time: f.preferred_time || null,
            p_source:        'hub',
            p_reporter_name: `${f.first_name} ${f.last_name}`.trim() || null,
            p_reporter_phone: f.phone || null,
            p_tags:          [],
          })
        // CELL (M28): production only — the cell path is the isCell() branch above, which returns first.
        : await supabase.rpc('ticket_file', {
        p_location_id:   proCtx.locationId,
        p_category:      f.category,
        p_priority:      f.urgency,                              // Low / Medium / High / Emergency, normalised to slugs
        p_title:         title,
        p_description:   `Issue location: ${f.suite}\n\n${f.description}`,
        p_suite_id:      suiteId,
        p_pro_id:        proCtx.proId,
        p_tenant_id:     proCtx.tenantId,
        p_photos:        photoUrls,
        p_preferred_time: f.preferred_time || null,
        p_source:        'hub',
        p_reporter_name: `${f.first_name} ${f.last_name}`.trim() || null,
        p_reporter_phone: f.phone || null,
        p_tags:          [],
      });
      if (fileErr) throw fileErr;
      const result = filed as { ticket_number?: string; merged?: boolean; photos_added?: number } | null;

      setTicketId(result?.ticket_number ?? '');
      setMerged(result?.merged ? mergedNote(result, {
        photos: photoUrls.length,
        preferredTime: Boolean(f.preferred_time),
        phone: Boolean(f.phone),
      }) : null);
      setStatus('success');
    } catch (err) {
      setStatus('error');
      setErrMsg((err as Error).message);
    }
  }

  if (status === 'success') {
    return (
      <div className="card text-center py-12">
        <div className="size-14 rounded-full bg-image-pink-gradient text-white flex items-center justify-center mx-auto mb-4 text-2xl">✓</div>
        <h2 className="font-display text-2xl font-bold text-image-black mb-2">Request Submitted</h2>
        <p className="text-sm text-image-charcoal max-w-sm mx-auto">
          {ticketId ? <>Ticket <span className="font-mono font-semibold text-image-pink">{ticketId}</span>. </> : ''}
          {merged
            ? <>{merged}</>
            : <>{isValidEmail(f.email) ? <>A confirmation email is on its way to {f.email}. </> : null}We'll be in touch within 24 hours (immediately for emergencies).</>}
        </p>
        <div className="flex gap-2 justify-center mt-6">
          {/* Who they are survives "Submit Another" — the prefill runs once, so clearing it here
              used to leave a second request with no name, phone or confirmation address. */}
          <button onClick={() => { setF(p => ({ ...EMPTY, first_name: p.first_name, last_name: p.last_name, phone: p.phone, email: p.email, suite: p.suite })); setStatus('idle'); }} className="btn-ghost">
            Submit Another
          </button>
          <a href="/my-info" className="btn-primary !bg-image-pink hover:!bg-image-plum">
            Back to My Info
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div>
        <h1 className="font-display text-2xl font-bold text-image-black">Maintenance <span className="text-image-pink">Request</span></h1>
        <p className="text-sm text-image-charcoal mt-1">Submit a request and we'll get it handled. You'll receive a confirmation with your ticket number. Past tickets live on My Info.</p>
      </div>

      <form onSubmit={handleSubmit} className="card space-y-5">
        <Section title="Your Info">
          <Row2>
            <Field label="First Name" required error={errors.first_name}>
              <input className="input" value={f.first_name} onChange={e => set('first_name', e.target.value)} />
            </Field>
            <Field label="Last Name" required error={errors.last_name}>
              <input className="input" value={f.last_name} onChange={e => set('last_name', e.target.value)} />
            </Field>
          </Row2>
          <Row2>
            <Field label="Phone" required error={errors.phone}>
              <input className="input" inputMode="tel" placeholder="(555) 123-4567" value={f.phone} onChange={e => set('phone', formatPhone(e.target.value))} />
            </Field>
            <Field label="Confirmation goes to">
              {/* HUB-067: shown, not editable — the request never carried a typed address. */}
              <p className="input !bg-image-fog/40 text-image-charcoal" data-testid="ticket-confirm-email">
                {isValidEmail(f.email) ? f.email : 'No email on file'}
              </p>
              <a href="/my-info" className="text-[11px] text-image-charcoal underline">Change it in My Info</a>
            </Field>
          </Row2>
          <Field label="Suite Number" required error={errors.suite}>
            {/* THE PRO'S OWN SUITE + COMMON AREAS, not the whole studio (feedback 151676d4,
                Phil: "Shouldn't it only allow me to select my suite and/or common areas only?").
                A pro files about their own room or a shared space — every other suite is
                somebody else's room. The full per-location list appears ONLY when we don't
                know their suite, because a pro with no suite on record must still be able
                to file rather than being locked out of the form. */}
            <BrandSelect value={f.suite} onChange={v => set('suite', v)} ariaLabel="Suite"
              placeholder="Select your suite…"
              options={[...(proCtx?.suiteNumber ? [`Suite ${proCtx.suiteNumber}`] : suiteLabels), ...COMMON_AREAS]} />
          </Field>
        </Section>

        <Section title="The Issue">
          <Field label="Category (pick the one that fits best)" required error={errors.category}>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5 max-h-60 overflow-auto rounded-lg border border-image-border bg-image-fog p-3" role="radiogroup" aria-label="Category">
              {categories.map(c => (
                <label key={c.slug} className="ph-choice flex items-center gap-2 cursor-pointer">
                  <input type="radio" name="category" value={c.slug} checked={f.category === c.slug}
                    onChange={() => set('category', c.slug)} className="size-3.5 accent-image-pink" />
                  <span>{c.label}</span>
                </label>
              ))}
            </div>
            <p className="text-xs text-image-dim mt-1">One category per request — if two things are wrong, send two requests so each reaches the right person.</p>
          </Field>

          <Field label="Urgency" required error={errors.urgency}>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              {URGENCIES.map(u => (
                <button
                  key={u.value} type="button"
                  onClick={() => set('urgency', u.value)}
                  className={`ph-choice px-3 py-2 rounded-lg border transition-colors ${
                    f.urgency === u.value
                      ? 'bg-image-pink text-white border-image-pink'
                      : 'bg-white text-image-black border-image-border hover:border-image-pink/40'
                  }`}
                >
                  {u.label}
                </button>
              ))}
            </div>
          </Field>

          {f.urgency === 'Emergency' && (
            <div className="bg-red-50 text-red-800 text-xs rounded-lg p-3 border border-red-200">
              <strong>⚠️ Emergency Request</strong><br />
              For immediate emergencies (flooding, fire, gas, security), also call the studio's line directly — someone is reached at any hour.
            </div>
          )}

          <Field label="Describe the Issue" required error={errors.description}>
            <textarea className="input" rows={4} maxLength={1000}
              placeholder="What's happening? When did it start? Is it affecting your ability to work?"
              value={f.description} onChange={e => set('description', e.target.value)} />
          </Field>

          <Field label="Best Time for Repair">
            <BrandSelect value={f.preferred_time} onChange={v => set('preferred_time', v)} ariaLabel="Preferred time"
              options={TIME_SLOTS.map(t => ({ value: t, label: t || 'No preference' }))} />
          </Field>

          <Field label="Photos (up to 3)">
            <label className="block border-2 border-dashed border-image-border rounded-xl p-4 text-center cursor-pointer hover:border-image-pink/50 transition-colors">
              {/* Drawn glyph, not an emoji: every other icon in the Hub is an SVG, and a system
                  emoji renders differently on each device — it was the one element on this form
                  that looked pasted in. */}
              <svg className="mx-auto mb-1 size-7 text-image-pink" viewBox="0 0 24 24" fill="none"
                   stroke="currentColor" strokeWidth="1.6" aria-hidden>
                <path d="M3 8.5A2.5 2.5 0 0 1 5.5 6h1.2a1.5 1.5 0 0 0 1.3-.75l.6-1A1.5 1.5 0 0 1 9.9 3.5h4.2a1.5 1.5 0 0 1 1.3.75l.6 1A1.5 1.5 0 0 0 17.3 6h1.2A2.5 2.5 0 0 1 21 8.5v8A2.5 2.5 0 0 1 18.5 19h-13A2.5 2.5 0 0 1 3 16.5v-8Z"/>
                <circle cx="12" cy="12.5" r="3.2"/>
              </svg>
              <div className="text-xs text-image-charcoal">
                <span className="text-image-pink font-medium">Add Photos</span> · auto-compressed
              </div>
              <input type="file" accept="image/*" multiple className="hidden"
                onChange={e => onPhotoChange(e.target.files)}
                disabled={f.photos.length >= 3} />
            </label>
            {f.photos.length > 0 && (
              <div className="grid grid-cols-3 gap-2 mt-2">
                {f.photos.map((src, i) => (
                  <div key={i} className="relative aspect-square rounded-lg overflow-hidden">
                    <img src={src} alt="" className="w-full h-full object-cover" />
                    <button type="button" onClick={() => set('photos', f.photos.filter((_, j) => j !== i))}
                      className="absolute top-1 right-1 size-6 bg-image-black/80 text-white rounded-full text-xs">×</button>
                  </div>
                ))}
              </div>
            )}
          </Field>
        </Section>

        <input type="text" name="_hp" tabIndex={-1} autoComplete="off" value={f._hp} onChange={e => set('_hp', e.target.value)} className="absolute -left-[9999px] opacity-0 w-0 h-0" />

        <button type="submit" className="btn-primary w-full !bg-image-pink hover:!bg-image-plum" disabled={status === 'loading'}>
          {status === 'loading' ? 'Submitting…' : 'Submit Request'}
        </button>

        {status === 'error' && (
          <div className="text-sm text-red-600 bg-red-50 rounded-lg p-3">Couldn't submit. {errMsg}</div>
        )}
      </form>
    </div>
  );
}

/* ---- helpers ---- */

async function compressImage(file: File, maxDim = 1600, quality = 0.8): Promise<string> {
  const img = await readImage(file);
  const ratio = Math.min(1, maxDim / Math.max(img.width, img.height));
  const w = Math.round(img.width * ratio); const h = Math.round(img.height * ratio);
  const canvas = document.createElement('canvas'); canvas.width = w; canvas.height = h;
  const ctx = canvas.getContext('2d')!; ctx.drawImage(img, 0, 0, w, h);
  return canvas.toDataURL('image/jpeg', quality);
}
function readImage(file: File): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const img = new Image();
      img.onload = () => resolve(img);
      img.onerror = reject;
      img.src = reader.result as string;
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div>
      <h3 className="ph-sec pb-2 mb-3 border-b border-image-border flex items-center gap-2">
        <span className="size-5 rounded-full bg-image-pink-gradient text-white flex items-center justify-center text-[10px] font-bold">●</span>
        {title}
      </h3>
      <div className="space-y-3">{children}</div>
    </div>
  );
}
function Row2({ children }: { children: React.ReactNode }) {
  return <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">{children}</div>;
}
function Field({ label, required, error, children }: { label: string; required?: boolean; error?: string; children: React.ReactNode }) {
  return (
    <div>
      <label className="ph-label mb-1">
        {label} {required && <span className="text-image-pink">*</span>}
      </label>
      {children}
      {error && <div className="text-[11px] text-red-600 mt-1">{error}</div>}
    </div>
  );
}

/**
 * HUB-024 (Tenean review 2026-10-06) · WHAT A MERGED REPORT SAYS. A second report on the same suite
 * and problem joins the open ticket. It used to promise that nothing went missing while the database kept only
 * the words — the photos, preferred time and phone were dropped. The database now keeps them
 * (image-studios-backend 20261011234100) and answers `photos_added`; this says only what the answer
 * proves. An answer without `photos_added` comes from the old door, which kept the words alone, so
 * the pro is told to pass the rest on.
 */
export function mergedNote(
  result: { photos_added?: number } | null,
  sent: { photos: number; preferredTime: boolean; phone: boolean },
): string {
  const base = 'This was added to a request already open for the same suite and problem, and the studio sees both reports on it.';
  if (typeof result?.photos_added !== 'number') {
    const extras = [
      sent.photos > 0 ? (sent.photos === 1 ? 'your photo' : 'your photos') : '',
      sent.preferredTime ? 'your preferred time' : '',
      sent.phone ? 'your phone number' : '',
    ].filter(Boolean);
    return extras.length
      ? `${base} Your description is on it, but ${extras.join(', ')} did not carry over — please give them to the studio directly.`
      : `${base} Your description is on it.`;
  }
  const kept = [
    'your description',
    sent.photos > 0 ? (sent.photos === 1 ? 'your photo' : 'your photos') : '',
    sent.preferredTime ? 'your preferred time' : '',
    sent.phone ? 'your phone number' : '',
  ].filter(Boolean);
  const list = kept.length > 1 ? `${kept.slice(0, -1).join(', ')} and ${kept[kept.length - 1]}` : kept[0];
  return `${base} ${list.charAt(0).toUpperCase()}${list.slice(1)} ${kept.length > 1 ? 'are' : 'is'} on it.`;
}
